import { previewIds } from "fe-shared/utils/generators";
import isEmail from "fe-shared/utils/isEmail";
import { restartWebsocket } from "actions/config";
import {
  ADD_PREVIEW,
  TIMEOUT_CODE,
  WEBBOT_CLEAR_MESSAGES,
  WEBBOT_CONNECTED,
  WEBBOT_CONNECTING,
  WEBBOT_CONNECTION_ERROR,
  WEBBOT_MESSAGE_SENT_TO_SDK,
  WEBBOT_PREVIOUS_MESSAGES,
  WEBBOT_SAVE_CVARS,
  WEBVIEW_DISABLED_INPUT,
  WEBVIEW_SKYP_USER_INPUT,
} from "actions/const";
import config from "config";
import { OWNER_POST_MESSAGE, urlRegex } from "constants";
import { getPreviews } from "reducers/webbot";
import { createAction } from "redux-actions";
import { decodeString, encodeString, isFromWidgetScript, isIE } from "utils";
import uuid1 from "uuid/v1";

import { checkStorageAvailability, removeLocalStorage } from "utils/storage";
import doAction from "./postMessageActions";
import postMessageBeforeSend from "./postMessageActions/postMessageBeforeSend";
import postMessageBeforeDisplay from "./postMessageActions/postMessageReceivedFromSdk";

let userId = "";
let botId = "";
let webchatKey = "";
let existsListenerMessageAlready = false;
let sumPreviousWaits = 0;
let numMessagesReceived = 0;
window.webbotConnection = null;
window.noDelayMode = false;

export const getWebchatKey = () => webchatKey;
export const getBotId = () => botId;
const connecting = createAction(WEBBOT_CONNECTING);
const isInputDisabled = createAction(WEBVIEW_DISABLED_INPUT);
const isSkipUserInput = createAction(WEBVIEW_SKYP_USER_INPUT);
const messageSentToSdk = createAction(WEBBOT_MESSAGE_SENT_TO_SDK);
export const clearMessages = createAction(WEBBOT_CLEAR_MESSAGES);
const setPreviousConversation = createAction(WEBBOT_PREVIOUS_MESSAGES);
const addPreview = createAction(ADD_PREVIEW);

const fetchPreview = url => {
  const mergedUrl = `/api/frontend/url/content/?u=${userId}&q=${url}`;
  const method = "GET";

  return fetch(mergedUrl, {
    method,
  }).then(response => response.json());
};

const getUrls = text =>
  text
    ? [...new Set(text.match(new RegExp(urlRegex, "gi")))].filter(
        value => !isEmail(value)
      )
    : [];

const getPreviewsFromMessage = text => (dispatch, getState) => {
  const urls = getUrls(text);
  const state = getState();
  const previewsList = getPreviews(state);

  const checkUrlInPreviews = url => !previewsList.some(item => item === url);

  urls.forEach(url => {
    if (checkUrlInPreviews(url)) {
      return fetchPreview(url).then(response => {
        const updatedResponse = response;
        updatedResponse.requestUrl = url;
        updatedResponse.id = -previewIds.next().value;

        dispatch(addPreview(updatedResponse));
      });
    }

    return url;
  });
};

export const parsedMessageReceived = data => dispatch => {
  const dataWithUrls = JSON.parse(data);

  dataWithUrls.message.actions = dataWithUrls.message.actions.map(action => {
    const result = action;
    const hasUrls = getUrls(action.text) && getUrls(action.text).length > 0;

    if (hasUrls) {
      result.urls = getUrls(action.text);

      dispatch(getPreviewsFromMessage(action.text));
    }

    return result;
  });

  dispatch(isSkipUserInput(dataWithUrls.message.skip_user_input));
  dispatch(isInputDisabled(true));

  if (isFromWidgetScript(window.location.href)) {
    dispatch(messageSentToSdk({ ...dataWithUrls, botId, webchatKey }));
    return;
  }

  postMessageBeforeDisplay({
    data: {
      payload: { ...dataWithUrls, botId, webchatKey },
    },
    functions: {
      dispatch,
    },
  });
};

function sendMessage({
  label,
  message = "",
  save = true,
  ref = null,
  showLabel = false,
  dataReceived,
  isSuggestedReply,
  visible = true,
}) {
  const sendText = message === "" ? label : message;
  const createData = text => ({
    message: {
      from: "human",
      user_profile: {},
      ref,
      user_id: userId || `webbot:${uuid1()}`,
      timestamp: Math.round(Date.now() / 1000),
      attachments: [],
      user_cvars: dataReceived?.userCvars,
      id: Date.now().toString(),
      actions: [
        {
          type: "text",
          text,
          isSuggestedReply,
        },
      ],
    },
  });

  const data = createData(sendText);
  const dataWithLabel = createData(label);

  return dispatch => {
    if (isFromWidgetScript(window.location.href)) {
      window.top.postMessage(
        {
          message: "on-message-before-send",
          payload: data,
        },
        "*"
      );
    } else {
      postMessageBeforeSend({
        data: {
          payload: data,
        },
      });
    }

    if (save && visible) {
      dispatch(
        parsedMessageReceived(JSON.stringify(showLabel ? dataWithLabel : data))
      );
    }
  };
}

const saveCVars = data => dispatch => {
  dispatch({ type: WEBBOT_SAVE_CVARS, data });
};

function sendCvars(data) {
  const { custom_vars } = data;

  const cvarsNames = Object.keys(custom_vars);
  const existCvars = cvarsNames && cvarsNames.length > 0;

  const createData = newCvars =>
    JSON.stringify({
      message: {
        user_id: userId || `webbot:${uuid1()}`,
        user_profile: {},
        actions: [
          {
            type: "sdk",
          },
        ],
        custom_vars: existCvars ? newCvars : {},
      },
    });

  const result = createData(custom_vars);

  return dispatch => {
    if (window.webbotConnection) {
      window.webbotConnection.send(result);
      dispatch(saveCVars(JSON.stringify(custom_vars)));
    }
  };
}

function connected({
  conn,
  customWebsocket,
  receivedUserId,
  receivedReferral,
  userCvars,
}) {
  window.webbotConnection = conn;
  if (receivedUserId) {
    userId = receivedUserId;
  } else {
    userId = `webbot:${uuid1()}`;
  }

  return dispatch => {
    dispatch({ type: WEBBOT_CONNECTED });
    if (!customWebsocket) {
      dispatch(
        sendMessage({
          label: "",
          message: "",
          ref: receivedReferral,
          dataReceived: { userCvars },
        })
      );
    }
  };
}

const closeConnectionWS = () => {
  if (window.webbotConnection) {
    window.webbotConnection.close();
  }
};

const addEventListenerForPostMessage = () => dispatch => {
  if (!existsListenerMessageAlready) {
    existsListenerMessageAlready = true;
    const doEvent = event => {
      const {
        data: { owner, data },
      } = event;
      if (owner === OWNER_POST_MESSAGE) {
        doAction({
          data,
          functions: {
            dispatch,
            parsedMessageReceived,
            sendMessage,
            sendCvars,
          },
        });
      }
    };
    window.addEventListener("message", doEvent);
  }
};

const messageReceive = message => dispatch =>
  dispatch(parsedMessageReceived(message.data));

const connectionError = e => {
  console.log("Connection error");
  console.log(e);
  print("Connection error", e);
  window.webbotConnection = null;
  return {
    payload: "Connection error",
    type: WEBBOT_CONNECTION_ERROR,
  };
};

const connectionClosed = () => {
  window.webbotConnection = null;
  return {
    payload: "Connection closed",
    type: WEBBOT_CONNECTION_ERROR,
  };
};

function getWsUri(id, newChannel = false) {
  if (newChannel) {
    const {
      azure: { channelsWs },
    } = config;
    return `${channelsWs}/webchat/${id}/`;
  }
  const { websocket } = config;
  let url = (window.location.protocol === "https:" && "wss://") || "ws://";
  url += window.location.host;
  if (websocket) {
    const {
      isDev,
      isStaging,
      isPreproduction,
      isProduction,
      websocketDev,
      websocketStaging,
      websocketPreproduction,
      websocketProduction,
    } = websocket;
    if (isDev) {
      url = websocketDev;
    } else if (isStaging) {
      url = websocketStaging;
    } else if (isPreproduction) {
      url = websocketPreproduction;
    } else if (isProduction) {
      url = websocketProduction;
    }
  }
  url += `/webbot/api/webchat/${id}/`;
  return url;
}

const logicToPersistUserSession =
  (id, receivedUserId, receivedReferral, testing) => dispatch => {
    const encodedId = encodeString(id);
    let customWebsocket = false;
    let receivedOrSavedUserId = receivedUserId || userId;

    const localStorageAvailability = checkStorageAvailability("localStorage");

    if (localStorageAvailability) {
      if (!isIE()) {
        if (testing) {
          removeLocalStorage(id, webchatKey);
        } else {
          const allMessages = localStorage.getItem(
            `${encodeString(`allMessages-${id}-${webchatKey}`)}`
          );
          if (allMessages) {
            customWebsocket = true;
            dispatch(setPreviousConversation({ botId, webchatKey }));
          }
        }
        if (receivedUserId) {
          removeLocalStorage(id, webchatKey);
        } else {
          receivedOrSavedUserId = localStorage.getItem(
            `${encodeString(`userId-${id}-${webchatKey}`)}`
          );
          if (!receivedOrSavedUserId) {
            try {
              receivedOrSavedUserId = `webbot:${uuid1()}`;
              localStorage.setItem(
                `${encodeString(`userId-${id}-${webchatKey}`)}`,
                encodeString(receivedOrSavedUserId)
              );
              localStorage.setItem(
                `${encodeString(`botId-${id}-${webchatKey}`)}`,
                encodedId
              );
            }
            catch (e) {
              console.log("Local Storage is full, Please empty data");
            }
          } else {
            receivedOrSavedUserId = decodeString(receivedOrSavedUserId);
          }
        }
        if (receivedReferral) {
          customWebsocket = false;
        }
        return { customWebsocket, receivedOrSavedUserId };
      }
    }

    return { customWebsocket, receivedOrSavedUserId };
  };

const makeConnection = ({
  id,
  receivedUserId,
  receivedReferral,
  testing,
  newChannel,
  noDelay = false,
  cvars,
  webchatKey: key,
  voiceEnabled,
  web,
}) => {
  botId = id;
  if (key) {
    webchatKey = key;
  }
  const isCustomWebsocket = id.startsWith("ws://") || id.startsWith("wss://");
  const wsUri = isCustomWebsocket ? id : getWsUri(id, newChannel);

  let prevUserId = null;

  return dispatch => {
    dispatch(connecting);
    window.noDelayMode = noDelay;
    let timeoutForLastMessage = false;

    const conn = new WebSocket(wsUri);
    console.log("Connection: ", conn);
    const customUserCvars = { ...cvars, web };

    conn.onopen = () => {
      console.log("Connection opened");
      const { customWebsocket, receivedOrSavedUserId } = dispatch(
        logicToPersistUserSession(id, receivedUserId, receivedReferral, testing)
      );

      prevUserId = receivedOrSavedUserId;

      window.top.postMessage(
        {
          message: "conversation-started",
          data: {
            botId,
            userId: receivedOrSavedUserId,
            webchatKey,
          },
        },
        "*"
      );

      dispatch(
        connected({
          conn,
          customWebsocket,
          receivedUserId: receivedOrSavedUserId,
          receivedReferral,
          userCvars: customUserCvars,
        })
      );

      dispatch(addEventListenerForPostMessage());
    };
    conn.onclose = e => {
      console.log("Connection closed");
      const closeCode = e.code.toString();

      dispatch(connectionClosed());
      window.top.postMessage(
        {
          message: "conversation-ended",
        },
        "*"
      );

      if (closeCode === TIMEOUT_CODE) {
        const dataToWebsocket = {
          id,
          userId: prevUserId,
          origin,
          cvars: customUserCvars,
          webchatKey,
        };

        restartWebsocket({
          dataToWebsocket,
          dataToResetConversation: {},
          forceReset: false,
        })(dispatch);
      }
    };
    conn.onerror = e => {
      console.log("Connection error", e);
    };

    conn.onmessage = function (result) {
      console.log(result.data);
    };

    conn.onmessage = e => {
      if (numMessagesReceived === 0) {
        window.top.postMessage(
          {
            message: "on-chat-started",
          },
          "*"
        );
        numMessagesReceived += 1;
      }
      dispatch(messageReceive(e));
      clearTimeout(timeoutForLastMessage);
      let timeToWait = 0;

      const data = JSON.parse(e.data);
      const { message } = data;
      message.actions.forEach(action => {
        if (action.type === "wait") {
          timeToWait += action.wait;
        }
      });
      const { cvars: messageCvars } = message;
      delete message.cvars;

      setTimeout(() => {
        window.top.postMessage(
          {
            actionName: message?.bot_module
              ? `${message.bot_module.id}`
              : undefined,
            message,
            cvars: messageCvars,
          },
          "*"
        );
      }, sumPreviousWaits);
      sumPreviousWaits += timeToWait;

      if (voiceEnabled) {
        window.top.postMessage(
          {
            message: "waiting-voice-to-continue",
          },
          "*"
        );
        timeoutForLastMessage = setTimeout(() => {
          window.top.postMessage(
            {
              message: "listen-micro",
              startAutomatically: true,
            },
            "*"
          );
          sumPreviousWaits = 0;
        }, sumPreviousWaits || 400);
      } else {
        timeoutForLastMessage = setTimeout(() => {
          sumPreviousWaits = 0;
        }, sumPreviousWaits || 400);
      }
    };
  };
};

export default {
  makeConnection,
  sendMessage,
  sendCvars,
  closeConnectionWS,
  saveCVars,
  clearMessages,
};
